/**
 * Dane do szablonów ofert i protokołów (pola + bloki) — jeden kod dla PDF na serwerze i podglądu w edytorze.
 * Źródłem są wyłącznie niezmienne treści: migawka wydanej oferty i podpisana treść protokołu.
 */
import { logoInput } from './doc-images';
import { renderTemplate, type RenderInput, type RenderResult } from './doc-render';
import { formatMoney } from './offers';
import { DEFAULT_PROTOCOL_TEMPLATES, PROTOCOL_KINDS, type ProtocolContent, protocolSections, type ProtocolTemplateDef, type SignatureData } from './protocol';
import type { OfferSnapshot, SnapshotItem, SnapshotVariant } from './types-offers';

export const escHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const nl = (s: string) => escHtml(s).replace(/\n/g, '<br>');
const qty = (n: number) => new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 3 }).format(n);
const longDate = (iso: string) => new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', dateStyle: 'long' }).format(new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso));
const dateTime = (iso: string) => new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', dateStyle: 'long', timeStyle: 'short' }).format(new Date(iso));

/** Strona A4 dokumentu z treścią szablonu (styl wspólny dla podglądu i PDF). */
export function documentPage(bodyHtml: string, opts: { title: string; color: string; draft: boolean; font?: 'serif' | 'sans' }): string {
  const body = opts.font === 'sans' ? "'DejaVu Sans', 'Noto Sans', Arial, sans-serif" : "'DejaVu Serif', 'Noto Serif', Georgia, serif";
  return `<!doctype html><html lang="pl"><head><meta charset="utf-8"><title>${escHtml(opts.title)}</title>
<style>
  @page { size: A4; margin: 18mm 18mm 22mm 18mm; }
  body { font-family: ${body}; font-size: ${opts.font === 'sans' ? '9.5pt' : '10.5pt'}; line-height: 1.45; color: #111; margin: 0; }
  h1, h2, h3 { font-family: 'DejaVu Sans', Arial, sans-serif; }
  h1 { font-size: 15pt; text-align: center; margin: 0 0 4px; }
  h2 { font-size: 12pt; margin: 16px 0 6px; }
  h3 { font-size: 11pt; margin: 12px 0 4px; }
  p { margin: 0 0 8px; text-align: justify; }
  .bar { height: 5px; background: ${escHtml(opts.color)}; margin-bottom: 14px; }
  table.dok-tabela { border-collapse: collapse; width: 100%; margin: 8px 0 12px; font-family: 'DejaVu Sans', Arial, sans-serif; font-size: 9pt; }
  table.dok-tabela th, table.dok-tabela td { border: 1px solid #c8ccd0; padding: 4px 6px; text-align: left; vertical-align: top; }
  table.dok-tabela th { background: #f2f4f6; }
  table.dok-tabela .n { text-align: right; white-space: nowrap; }
  table.dok-tabela tr.suma td { font-weight: bold; }
  table.dok-tabela .slownie { font-style: italic; }
  table.dok-sumy { border-collapse: collapse; width: 55%; margin: 4px 0 12px auto; font-family: 'DejaVu Sans', Arial, sans-serif; font-size: 9pt; }
  table.dok-sumy td { padding: 2px 6px; }
  table.dok-sumy .n { text-align: right; white-space: nowrap; }
  table.dok-sumy tr.suma td { font-weight: bold; font-size: 11pt; border-top: 2px solid #111; padding-top: 4px; }
  .wariant { page-break-inside: avoid; }
  .wariant h2 { color: ${escHtml(opts.color)}; }
  .opis { color: #555; font-size: 8.5pt; }
  img.dok-logo { max-height: 22mm; max-width: 70mm; vertical-align: middle; }
  img.dok-grafika { height: auto; max-width: 100%; vertical-align: middle; }
  .brak-pola { background: #ffe3e3; color: #a00; padding: 0 2px; }
  .podpisy { display: flex; justify-content: space-between; margin-top: 48px; }
  .draft { position: fixed; top: 40%; left: 12%; font-size: 64pt; color: rgba(200,0,0,.10); transform: rotate(-30deg); }
</style></head><body>${opts.draft ? '<div class="draft">PROJEKT</div>' : ''}<div class="bar"></div>
${bodyHtml}
</body></html>`;
}

/** Blok, którego szablon nie wstawił, a który jest częścią dokumentu — dopisywany na końcu. */
function withRequiredBlocks(template: string, keys: string[]): string {
  const missing = keys.filter((k) => !new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`).test(template));
  return missing.length ? `${template}\n${missing.map((k) => `{{${k}}}`).join('\n')}` : template;
}

// ───────────── oferta ─────────────

const discountText = (i: SnapshotItem) =>
  !i.discount || i.discount.value <= 0 ? '' : i.discount.type === 'procent' ? `${String(i.discount.value).replace('.', ',')}%` : formatMoney(i.discount.value);

function itemRow(i: SnapshotItem, withPrices: boolean): string {
  const comps = i.components.length ? `<div class="opis">W skład wchodzi: ${i.components.map((c) => `${escHtml(c.name)} (${qty(c.quantity)} ${escHtml(c.unit)})`).join('; ')}</div>` : '';
  const desc = i.description ? `<div class="opis">${nl(i.description)}</div>` : '';
  return `<tr><td>${escHtml(i.name)}${desc}${comps}</td><td class="n">${qty(i.quantity)} ${escHtml(i.unit)}</td>${
    withPrices ? `<td class="n">${formatMoney(i.unitPriceGr, false)}</td><td class="n">${discountText(i)}</td>` : ''
  }<td class="n">${i.vatRate}%</td>${withPrices ? `<td class="n">${formatMoney(i.netGr, false)}</td>` : ''}</tr>`;
}

function variantBlock(v: SnapshotVariant, snap: OfferSnapshot, index: number, total: number): string {
  const withPrices = v.presentation === 'szczegolowa';
  const scopeNames = v.scopeIds
    .map((id) => snap.scopes.find((s) => s.id === id)?.tradeName)
    .filter(Boolean)
    .join(', ');
  const vat = v.totals.vatByRate.map((r) => `<tr><td>VAT ${r.rate}% od ${formatMoney(r.netGr)}</td><td class="n">${formatMoney(r.vatGr)}</td></tr>`).join('');
  const variantDiscount =
    v.discount && v.discount.value > 0
      ? `<p class="opis">Uwzględniono rabat na całość: ${v.discount.type === 'procent' ? `${String(v.discount.value).replace('.', ',')}%` : formatMoney(v.discount.value)}.</p>`
      : '';
  return `<div class="wariant">
<h2>${total > 1 ? `Wariant ${index + 1}: ` : ''}${escHtml(v.name)}</h2>
${scopeNames ? `<div class="opis">Zakres: ${escHtml(scopeNames)}</div>` : ''}
${v.description ? `<p>${nl(v.description)}</p>` : ''}
<table class="dok-tabela"><thead><tr><th>Pozycja</th><th class="n">Ilość</th>${withPrices ? '<th class="n">Cena netto</th><th class="n">Rabat</th>' : ''}<th class="n">VAT</th>${
    withPrices ? '<th class="n">Wartość netto</th>' : ''
  }</tr></thead><tbody>${v.items.map((i) => itemRow(i, withPrices)).join('')}</tbody></table>
${variantDiscount}
<table class="dok-sumy"><tbody><tr><td>Razem netto</td><td class="n">${formatMoney(v.totals.netGr)}</td></tr>${vat}<tr class="suma"><td>Razem brutto</td><td class="n">${formatMoney(v.totals.grossGr)}</td></tr></tbody></table>
</div>`;
}

/** opts.logoSrc — logo firmy jako data:image/… (serwer: z pliku logo z migawki; podgląd: logo wybranej firmy). */
export function offerTemplateInput(snap: OfferSnapshot, opts: { logoSrc?: string | null } = {}): RenderInput {
  const c = snap.company;
  const logo = logoInput(opts.logoSrc);
  const scopeNames = [...new Set(snap.scopes.map((s) => s.tradeName))].join(', ');
  return {
    fields: {
      'firma.nazwa': c.fullName,
      'firma.skrot': c.shortName,
      'firma.adres': c.address,
      'firma.nip': c.nip,
      'firma.regon': c.regon,
      'firma.krs': c.krs,
      'firma.rachunek': c.bankAccount,
      'firma.email': c.email,
      'firma.telefon': c.phone,
      'klient.nazwa': snap.client.displayName,
      'klient.adres': snap.client.address,
      'lokalizacja.adres': snap.site.address,
      'zakresy.nazwy': scopeNames,
      'oferta.numer': snap.number,
      'oferta.wersja': String(snap.versionNo),
      'oferta.data': longDate(snap.issuedAt),
      'oferta.wazna_do': longDate(snap.validUntil),
      'oferta.tytul': snap.title,
      'oferta.liczba_wariantow': String(snap.variants.length),
      'oferta.wiele_wariantow': snap.variants.length > 1 ? 'tak' : '',
      'oferta.warunki': snap.terms,
      'oferta.wartosc_brutto': snap.variants.length === 1 ? formatMoney(snap.variants[0]!.totals.grossGr) : '',
      'przygotowal.nazwa': snap.preparedBy.name,
      'przygotowal.telefon': snap.preparedBy.phone,
      'przygotowal.email': snap.preparedBy.email,
      ...logo.field,
    },
    blocks: {
      ...logo.block,
      warianty: snap.variants.map((v, i) => variantBlock(v, snap, i, snap.variants.length)).join('\n'),
      warunki: snap.terms ? `<p>${nl(snap.terms)}</p>` : '',
    },
  };
}

/** Oferta z szablonu: treść (bez strony A4) i pola nieznane. Warianty są obowiązkowe — dopisywane, jeśli ich brak. */
export function renderOfferTemplate(bodyHtml: string, snap: OfferSnapshot, opts: { logoSrc?: string | null } = {}): RenderResult {
  return renderTemplate(withRequiredBlocks(bodyHtml, ['warianty']), offerTemplateInput(snap, opts));
}

// ───────────── protokół ─────────────

/** Podpis z przebiegu kreślenia (SVG) — ten sam zapis, który jest powiązany ze skrótem treści. */
export function signatureSvg(s: SignatureData): string {
  const paths = s.strokes
    .filter((st) => st.length > 0)
    .map((st) => (st.length === 1 ? `M${st[0]![0]} ${st[0]![1]} l0.1 0` : `M${st.map(([x, y]) => `${x} ${y}`).join(' L')}`))
    .map((d) => `<path d="${d}" fill="none" stroke="#0b2a6b" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>`)
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 400" width="300" height="120">${paths}</svg>`;
}

export interface ProtocolDocInput {
  content: ProtocolContent;
  template: ProtocolTemplateDef;
  contentHash: string;
  /** podpis odręczny na ekranie; podpis zdalny — null (blok podpisu wg `remote`) */
  signature: SignatureData | null;
  photos: { sha256: string; dataUri: string | null }[];
  /**
   * Podpis zdalny (treść z signMode): stan w chwili tworzenia PDF. Bez podpisu — dokument do podpisu
   * (e-podpis: karta podpisów dołączana na końcu; papier: miejsce na podpis odręczny).
   */
  remote?: { method: 'epodpis' | 'papier'; signedAt: string | null; signerName: string; remarks: string; recordedBy?: string | null } | null;
  /** logo firmy (data:image/png|jpeg) — pole {{firma.logo}} i nagłówek układu wbudowanego */
  logoSrc?: string | null;
}

export function protocolTemplateInput(o: ProtocolDocInput): RenderInput {
  const c = o.content;
  const signedAt = dateTime(c.signedAt);
  const sections = protocolSections(c, o.template)
    .filter((s) => s.title !== 'Podpis')
    .map((s) => `<h2>${escHtml(s.title)}</h2><table class="dok-tabela"><tbody>${s.rows.map(([k, v]) => `<tr><th style="width:42%">${escHtml(k)}</th><td>${escHtml(v)}</td></tr>`).join('')}</tbody></table>`)
    .join('\n');
  const decl = o.template.declarations.length ? `<h2>Oświadczenia</h2>${o.template.declarations.map((d) => `<p>${escHtml(d)}</p>`).join('')}` : '';
  const hashRow = `<tr><th>Skrót treści (SHA-256)</th><td style="font-family:monospace;font-size:8pt;word-break:break-all">${escHtml(o.contentHash)}</td></tr>`;
  const r = o.remote;
  const remarksRow = r?.remarks.trim() ? `<tr><th>Uwagi klienta zgłoszone przy podpisie</th><td>${escHtml(r.remarks.trim())}</td></tr>` : '';
  const signature = !r
    ? `<div style="page-break-inside:avoid"><h2>Podpis</h2>
<table class="dok-tabela"><tbody>
<tr><th style="width:42%">Podpis klienta (${escHtml(c.signerName)})</th><td>${o.signature ? signatureSvg(o.signature) : ''}</td></tr>
<tr><th>Data podpisu</th><td>${escHtml(signedAt)}</td></tr>
<tr><th>Wykonał</th><td>${escHtml(c.installer.name)}</td></tr>
${hashRow}
</tbody></table>
<p class="opis">Podpis złożony odręcznie na ekranie urządzenia wykonawcy w obecności wykonawcy i powiązany ze skrótem treści protokołu. Nie jest to kwalifikowany podpis elektroniczny.</p></div>`
    : r.method === 'epodpis'
      ? `<div style="page-break-inside:avoid"><h2>Podpis</h2>
<table class="dok-tabela"><tbody>
<tr><th style="width:42%">Podpis klienta (${escHtml(r.signerName || c.signerName)})</th><td>podpis elektroniczny — karta podpisów na końcu dokumentu</td></tr>
<tr><th>Protokół przygotowano</th><td>${escHtml(signedAt)}</td></tr>
<tr><th>Wykonał</th><td>${escHtml(c.installer.name)}</td></tr>
${hashRow}
</tbody></table>
<p class="opis">Klient podpisuje protokół elektronicznie (podpis elektroniczny zwykły potwierdzony jednorazowym kodem). Uwagi zgłoszone przy podpisie, data podpisu i ślad dowodowy są w karcie podpisów. Nie jest to kwalifikowany podpis elektroniczny.</p></div>`
      : r.signedAt
        ? `<div style="page-break-inside:avoid"><h2>Podpis</h2>
<table class="dok-tabela"><tbody>
<tr><th style="width:42%">Podpis klienta (${escHtml(r.signerName || c.signerName)})</th><td>odręcznie na wydruku protokołu — skan podpisanego wydruku dołączony osobno</td></tr>
<tr><th>Data podpisu</th><td>${escHtml(dateTime(r.signedAt))}</td></tr>
<tr><th>Protokół przygotowano</th><td>${escHtml(signedAt)}</td></tr>
<tr><th>Wykonał</th><td>${escHtml(c.installer.name)}</td></tr>
${r.recordedBy ? `<tr><th>Podpis zarejestrował</th><td>${escHtml(r.recordedBy)}</td></tr>` : ''}
${remarksRow}
${hashRow}
</tbody></table></div>`
        : `<div style="page-break-inside:avoid"><h2>Podpis</h2>
<p>Potwierdzam odbiór prac opisanych w protokole${c.clientRemarks.trim() ? '' : ' (uwagi wpisz poniżej)'}.</p>
<p style="margin-top:6mm">Uwagi klienta: ……………………………………………………………………………………………………………</p>
<p>…………………………………………………………………………………………………………………………………</p>
<table class="dok-tabela" style="margin-top:8mm"><tbody>
<tr><th style="width:42%">Data</th><td style="height:12mm"></td></tr>
<tr><th>Czytelny podpis klienta (${escHtml(c.signerName)})</th><td style="height:22mm"></td></tr>
<tr><th>Wykonał</th><td>${escHtml(c.installer.name)} (protokół przygotowano ${escHtml(signedAt)})</td></tr>
${hashRow}
</tbody></table>
<p class="opis">Po podpisaniu prosimy o odesłanie skanu lub zdjęcia wydruku — trafi do systemu jako podpisany protokół ${escHtml(c.code)}.</p></div>`;
  const photos = o.photos.length
    ? `<div style="page-break-before:always"></div><h2>Zdjęcia (${o.photos.length})</h2>${o.photos
        .map(
          (p, i) =>
            `<div style="margin:0 0 10px;page-break-inside:avoid">${p.dataUri ? `<img src="${p.dataUri}" style="max-width:100%;max-height:105mm;display:block"/>` : '<p><i>plik niedostępny</i></p>'}<div class="opis">Zdjęcie ${i + 1} · SHA-256: ${escHtml(p.sha256)}</div></div>`,
        )
        .join('')}`
    : '';
  const trades = c.scope?.tradeName ?? c.title.split(' — ').slice(1).join(' — ');
  const logo = logoInput(o.logoSrc);
  return {
    fields: {
      ...logo.field,
      'firma.nazwa': c.company.name,
      'firma.adres': c.company.address,
      'firma.nip': c.company.nip,
      'klient.nazwa': c.client.name,
      'lokalizacja.adres': c.site.address,
      'zakresy.nazwy': trades,
      'zadanie.numer': c.task.number,
      'protokol.tytul': c.title,
      'protokol.rodzaj': PROTOCOL_KINDS[c.kind],
      'protokol.kod': c.code,
      'protokol.data_podpisu': r ? (r.signedAt ? dateTime(r.signedAt) : '') : signedAt,
      'protokol.podpisal': r?.signerName || c.signerName,
      'protokol.wykonal': c.installer.name,
      'protokol.uwagi_klienta': [c.clientRemarks.trim(), r?.remarks.trim() ?? ''].filter(Boolean).join(' / '),
      'protokol.skrot': o.contentHash,
    },
    blocks: { ...logo.block, protokol_sekcje: sections, oswiadczenia: decl, podpis: signature, zdjecia: photos },
  };
}

/** Układ wbudowany protokołu (gdy nie ma szablonu „Protokół”) — ten sam, który był przed szablonami. */
export const BUILTIN_PROTOCOL_BODY = `{{#if firma.ma_logo}}<p>{{firma.logo}}</p>{{/if}}<h1>{{protokol.tytul}}</h1>
<p style="text-align:center" class="opis">Identyfikator {{protokol.kod}} · zadanie {{zadanie.numer}}</p>
{{protokol_sekcje}}
{{oswiadczenia}}
{{podpis}}
{{zdjecia}}`;

/** Protokół z szablonu. Podpis i zdjęcia są obowiązkowe — dopisywane na końcu, jeśli szablon ich nie zawiera. */
export function renderProtocolTemplate(bodyHtml: string, o: ProtocolDocInput): RenderResult {
  return renderTemplate(withRequiredBlocks(bodyHtml, ['podpis', 'zdjecia']), protocolTemplateInput(o));
}

// ───────────── dane przykładowe (podgląd w edytorze) ─────────────

export const SAMPLE_OFFER_SNAPSHOT: OfferSnapshot = {
  schema: 1,
  number: 'OF/B/2026/0001',
  versionNo: 1,
  issuedAt: '2026-10-24T10:00:00Z',
  validUntil: '2026-11-23',
  title: 'Pompa ciepła powietrze-woda dla domu jednorodzinnego',
  terms: 'Termin realizacji do 6 tygodni od podpisania umowy.\nGwarancja na montaż 24 miesiące.',
  company: {
    code: 'B',
    shortName: 'Aurora Energia sp. z o.o.',
    fullName: 'Aurora Energia spółka z ograniczoną odpowiedzialnością',
    nip: '0000000000',
    regon: '000000000',
    krs: '0000000000',
    address: 'ul. Wiatrowa 3, 44-100 Gliwice',
    email: 'biuro@firma.example',
    phone: '32 000 00 00',
    bankAccount: '00 0000 0000 0000 0000 0000 0000',
    color: '#0b7d62',
  },
  client: { displayName: 'Marek Zieliński', address: 'ul. Lipowa 12, 43-190 Mikołów' },
  site: { address: 'ul. Lipowa 12, 43-190 Mikołów' },
  scopes: [{ id: 's1', tradeCode: 'PC', tradeName: 'Pompa ciepła' }],
  variants: [
    {
      id: 'v1',
      name: 'Pompa ciepła 10 kW — komplet',
      description: '',
      presentation: 'szczegolowa',
      discount: null,
      scopeIds: ['s1'],
      items: [
        { id: 'i1', scopeId: 's1', name: 'Pompa ciepła 10 kW', description: 'Jednostka zewnętrzna z modułem hydraulicznym', unit: 'szt.', quantity: 1, unitPriceGr: 2890000, discount: null, vatRate: 8, netGr: 2890000, components: [] },
        { id: 'i2', scopeId: 's1', name: 'Montaż i uruchomienie', description: '', unit: 'kpl.', quantity: 1, unitPriceGr: 600000, discount: null, vatRate: 8, netGr: 600000, components: [] },
      ],
      totals: { netGr: 3490000, vatGr: 279200, grossGr: 3769200, vatByRate: [{ rate: 8, netGr: 3490000, vatGr: 279200 }] },
    },
  ],
  preparedBy: { name: 'Karolina Wójcik', email: 'k.wojcik@firma.example', phone: '600 000 001' },
};

const sampleTpl = DEFAULT_PROTOCOL_TEMPLATES.find((t) => t.kind === 'montaz') ?? DEFAULT_PROTOCOL_TEMPLATES[0]!;
/** przykładowe wartości pól wg typu (liczba w dopuszczalnym zakresie, tak, pierwsza opcja) */
const sampleValues = Object.fromEntries(
  sampleTpl.sections.flatMap((s) =>
    s.fields.map((fd) => [fd.key, fd.type === 'liczba' ? (fd.min ?? 0) + Math.max(1, Math.round(((fd.max ?? (fd.min ?? 0) + 10) - (fd.min ?? 0)) / 2)) : fd.type === 'tak_nie' ? true : fd.type === 'wybor' ? (fd.options?.[0] ?? '') : 'zgodnie z projektem']),
  ),
);

export const SAMPLE_PROTOCOL: ProtocolDocInput = {
  template: sampleTpl,
  contentHash: '3f2a9c0d1b7e4a5f8c6d2e1f0a9b8c7d6e5f4a3b2c1d0e9f8a7b6c5d4e3f2a1b',
  signature: {
    strokes: [
      [
        [80, 260, 0],
        [160, 160, 40],
        [240, 280, 80],
        [330, 150, 120],
        [420, 270, 160],
        [520, 190, 200],
        [640, 240, 240],
      ],
    ],
  },
  photos: [],
  content: {
    format: 1,
    id: '00000000-0000-0000-0000-000000000000',
    code: 'PO-7F3K92',
    kind: sampleTpl.kind,
    title: `${PROTOCOL_KINDS[sampleTpl.kind]} — Pompa ciepła`,
    template: { code: sampleTpl.code, version: sampleTpl.version },
    task: { id: 't1', number: 'Z/2026/0001' },
    company: { name: 'Aurora Energia sp. z o.o.', nip: '0000000000', address: 'ul. Wiatrowa 3, 44-100 Gliwice' },
    client: { name: 'Marek Zieliński' },
    site: { address: 'ul. Lipowa 12, 43-190 Mikołów' },
    scope: { tradeCode: 'PC', tradeName: 'Pompa ciepła', description: '' },
    devices: sampleTpl.devices ? [{ name: 'Pompa ciepła 10 kW', serial: 'SN-000123', deviceId: null }] : [],
    values: sampleValues,
    checklist: [],
    remarks: '',
    clientRemarks: '',
    photos: [],
    email: 'klient@poczta.example',
    signerName: 'Marek Zieliński',
    installer: { id: 'u1', name: 'Tomasz Kruk' },
    signedAt: '2026-10-24T12:20:00Z',
  },
};
